/**
 * writeOffDuesModel — PURE state model for the C6b Write-off dues dialog
 * (FE-5). No React, no API calls: the amount is remainder-only (the SERVER's
 * `dues_paise`, never typed/re-derived by the client — mirrors
 * `duesCollectModel`'s single-source rule), so the only client-side field is
 * the mandatory reason. Given the reason text + the server's cached figures,
 * this derives:
 *
 *   - whether the confirm button is enabled + the exact ratified
 *     disabled-reason string (copy-deck §4 — every disabled control states why)
 *   - the optional "₹Y of the original ₹Z was collected earlier…" context line
 *   - the typed-error → ratified-copy mapping (§4/§6)
 *   - the ratified neutral success line
 *   - the two-step confirm modal's exact copy (§6)
 *
 * Every string here is pasted verbatim from `state/epics/C/C6b/copy-deck.md`
 * §4/§6 — no free-handing.
 */

import { formatPaise } from '../../../utils/money';
import { DUES_ERROR_CODE } from '../../../constants/admission';

/**
 * Minimum reason length the backend enforces (contract §3: `min_length:10`
 * after strip, else 422 REASON_REQUIRED). Enforced client-side too so the
 * confirm button doesn't invite a wasted round-trip — the disabled-reason
 * copy stays the same single ratified string regardless of how far short the
 * typed reason is.
 */
export const WRITEOFF_REASON_MIN_LENGTH = 10;

/**
 * writeOffReasonState — the ONE gate on the confirm button: reason must be
 * non-blank and meet the server's minimum length.
 * @param {string} reason typed reason text
 */
export function writeOffReasonState(reason) {
  const trimmed = (reason || '').trim();
  if (trimmed.length < WRITEOFF_REASON_MIN_LENGTH) {
    return {
      valid: false,
      helper: 'Enter a reason to write off this due.',
      helperKind: 'disabled-reason',
    };
  }
  return { valid: true, helper: null, helperKind: 'hint' };
}

/**
 * writeOffPartialContextLine — the copy-deck §4 "part-collected context"
 * line, shown only when SOME amount was already collected before the
 * write-off (`duesCollectedPaise > 0`). Returns null otherwise (including
 * when the field is simply absent on the caller's row shape — e.g. the
 * queue's `DuesPendingRow` doesn't carry `dues_collected_paise`, contract §2).
 *
 * @param {number} duesPaise          remaining outstanding (server, being written off)
 * @param {number} [duesCollectedPaise] amount already collected on this admission
 */
export function writeOffPartialContextLine({ duesPaise, duesCollectedPaise }) {
  const collected = Math.max(0, Math.round(Number(duesCollectedPaise) || 0));
  if (collected <= 0) return null;
  const remaining = Math.max(0, Math.round(Number(duesPaise) || 0));
  const original = remaining + collected;
  return `${formatPaise(collected)} of the original ${formatPaise(original)} was collected earlier. Only the remaining ${formatPaise(remaining)} is being written off.`;
}

/**
 * writeOffErrorMessage — typed server-error → ratified copy (§4). Callers
 * key off `code` only, never `error.message` text.
 *
 * @param {string} code   DUES_ERROR_CODE key
 * @param {object} extras passthrough fields off the 4xx/409 body (dues_paise)
 */
export function writeOffErrorMessage(code, extras = {}) {
  switch (code) {
    case DUES_ERROR_CODE.DUES_CHANGED:
      return `A payment was just recorded on this admission. The amount due is now ${formatPaise(extras.dues_paise)}. Review before writing off.`;
    case DUES_ERROR_CODE.DUES_NOT_OPEN:
      return 'This due is already closed. Nothing remains to write off.';
    case DUES_ERROR_CODE.REASON_REQUIRED:
      return 'Enter a reason to write off this due.';
    case DUES_ERROR_CODE.ADMISSION_NOT_FOUND:
      return 'This admission could not be found.';
    default:
      return null; // caller falls back to the API layer's generic error text
  }
}

/**
 * writeOffSuccessMessage — §4 neutral success line. Accepts either the full
 * `DuesWriteOffResponse` envelope ({ writeoff: {...} }) or, defensively, a
 * bare `{ written_off_paise }` shape.
 */
export function writeOffSuccessMessage(data) {
  const writeoff = (data && data.writeoff) || {};
  const amountPaise = writeoff.written_off_paise != null ? writeoff.written_off_paise : (data && data.written_off_paise);
  return `${formatPaise(amountPaise)} written off. This admission has left Payment Pending.`;
}

/**
 * writeOffConfirmCopy — the §6 second-step ConfirmationModal copy (title +
 * body + button labels), all naming the exact amount.
 */
export function writeOffConfirmCopy({ amountPaise, patientName }) {
  const amt = formatPaise(amountPaise);
  return {
    title: `Write off ${amt} for ${patientName}?`,
    message: `${amt} will be recorded as uncollectable. This is not a payment — no money is received and no receipt is issued. This cannot be reversed.`,
    ctaLabel: `Write off ${amt} as uncollectable`,
    cancelLabel: 'Go back',
  };
}
